import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldCheck, LogOut } from "lucide-react";
import { useAdminAuth, getAdminAuthHeaders } from "./AdminAuthContext";
import { translateAdminError } from "./adminErrors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminChangePasswordPage() {
  const { user, updateUser, adminLogout } = useAdminAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const forced = !!user?.must_change_password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) { setError("La nueva contraseña debe tener al menos 8 caracteres."); return; }
    if (newPassword !== confirmPassword) { setError("Las contraseñas no coinciden."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/auth/change-password`, { current_password: currentPassword, new_password: newPassword }, { headers: getAdminAuthHeaders() });
      updateUser({ must_change_password: false });
      navigate("/admin");
    } catch (err) {
      setError(translateAdminError(err.response?.data?.detail) || "No se pudo cambiar la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  if (!user) { navigate("/admin/login"); return null; }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4" data-testid="admin-change-password-page">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="h-12 w-12 rounded-xl bg-[#028CF8] flex items-center justify-center mb-3">
            <ShieldCheck className="h-6 w-6 text-white" />
          </div>
          <h1 className="font-bold text-lg text-slate-900">Cambiar contraseña</h1>
          {forced && (
            <p data-testid="admin-force-change-notice" className="text-xs text-amber-600 text-center mt-2">
              Debés cambiar tu contraseña temporal antes de continuar.
            </p>
          )}
        </div>
        {error && <div data-testid="admin-change-password-error" className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Contraseña actual</label>
            <input data-testid="admin-change-current-password-input" type="password" required value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full h-11 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Nueva contraseña</label>
            <input data-testid="admin-change-new-password-input" type="password" required value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full h-11 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Confirmar nueva contraseña</label>
            <input data-testid="admin-change-confirm-password-input" type="password" required value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full h-11 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]" />
          </div>
          <button type="submit" data-testid="admin-change-password-submit" disabled={loading}
            className="w-full h-11 rounded-lg bg-[#028CF8] hover:bg-[#0270C5] text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-70">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar contraseña"}
          </button>
        </form>
        {!forced && (
          <button onClick={() => { adminLogout(); navigate("/admin/login"); }} data-testid="admin-change-password-cancel"
            className="w-full mt-3 h-10 rounded-lg text-slate-500 text-sm flex items-center justify-center gap-2 hover:bg-slate-50">
            <LogOut className="h-3.5 w-3.5" /> Cancelar y salir
          </button>
        )}
      </div>
    </div>
  );
}
